import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import BenefitEvents from '../benefit/benefit_events';
import LinkifiedText from './linkified_text';
import {
    PageShell,
    PageHeader,
    HeaderCopy,
    PageTitle,
    PageDescription,
    PlacementTabs,
    PlacementTab,
    TabStatus,
    TabLabel,
    StatusMessage,
    ContentGrid,
    SettingsCard,
    PreviewCard,
    SectionHeader,
    SectionTitle,
    SectionDescription,
    SwitchRow,
    SwitchCopy,
    SwitchButton,
    SwitchKnob,
    FormSection,
    SubsectionHeading,
    SubsectionTitle,
    SubsectionDescription,
    FieldGroup,
    FieldLabel,
    Required,
    Optional,
    TypeOptions,
    TypeButton,
    TextInput,
    TextArea,
    FileInput,
    UploadArea,
    UploadCopy,
    UploadButton,
    FieldMetaRow,
    FieldHint,
    CharacterCount,
    DateGrid,
    StatusDot,
    ScheduleStrip,
    CardFooter,
    FooterMeta,
    SaveState,
    UpdatedInfo,
    FooterActions,
    PrimaryButton,
    ResetButton,
    SecondaryButton,
    StatePanel,
    VisibilityBadge,
    PreviewStage,
    PreviewViewport,
    PreviewBar,
    PreviewBrand,
    PreviewCanvas,
    PopupPreview,
    PreviewImage,
    ImagePlaceholder,
    PreviewTitle,
    PreviewBody,
    PreviewAction,
    PreviewNote
} from './popup_settings.styles';

// label은 탭 이름, popupLabel은 해당 슬롯 팝업의 이름이다
// (PRE_LOGIN_EVENT 탭은 가입혜택 이벤트 관리까지 담으므로 두 이름이 다르다).
export const PLACEMENT_META = {
    PRE_LOGIN: { label: '로그인 전', popupLabel: '로그인 전 팝업', description: '로그인 화면 일반 공지' },
    USER_HOME: { label: '고객 메인홈', popupLabel: '고객 메인홈 팝업', description: '로그인 후 고객 메인 화면' },
    MANAGER_ORDER_LIST: { label: '매니저 오더리스트', popupLabel: '매니저 오더리스트 팝업', description: '매니저 앱 오더 목록 화면' },
    PRE_LOGIN_EVENT: { label: '가입혜택관리', popupLabel: '가입혜택 팝업', description: '이벤트 조건·혜택·팝업을 한 흐름으로 관리' }
};

export const CONTENT_TYPE_LABEL = {
    IMAGE: '이미지',
    TEXT: '텍스트',
    IMAGE_TEXT: '이미지 + 텍스트'
};

const CONTENT_TYPE_DESCRIPTION = {
    IMAGE: '이미지만 선명하게 노출',
    TEXT: '제목과 본문 중심 안내',
    IMAGE_TEXT: '이미지와 안내를 함께 구성'
};

const STATUS_LABEL = {
    live: '노출 중',
    scheduled: '예약됨',
    expired: '종료됨',
    disabled: '비노출',
    incomplete: '기간 확인'
};

function toLocalDateTime(value) {
    return value ? value.slice(0, 16) : '';
}

function toApiDateTime(value) {
    return value ? `${value}:00+09:00` : '';
}

function parseKoreaDateTime(value) {
    if (!value) return NaN;
    const normalized = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)
        ? `${value}:00+09:00`
        : value;
    return Date.parse(normalized);
}

function formatKoreaDateTime(value) {
    const timestamp = parseKoreaDateTime(value);
    if (!Number.isFinite(timestamp)) return '기간을 설정해 주세요.';
    return new Intl.DateTimeFormat('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).format(timestamp);
}

export function getPopupScheduleState(popup, now = Date.now()) {
    if (!popup) {
        return { tone: 'incomplete', label: STATUS_LABEL.incomplete, detail: '팝업 설정 정보를 불러오지 못했습니다.' };
    }
    if (!popup?.enabled) {
        return { tone: 'disabled', label: STATUS_LABEL.disabled, detail: '노출 스위치가 꺼져 있습니다.' };
    }

    const startsAt = parseKoreaDateTime(popup.starts_at);
    const endsAt = parseKoreaDateTime(popup.ends_at);
    if (!Number.isFinite(startsAt) || !Number.isFinite(endsAt)) {
        return { tone: 'incomplete', label: STATUS_LABEL.incomplete, detail: '노출 시작·종료 일시를 확인해 주세요.' };
    }
    if (now < startsAt) {
        return {
            tone: 'scheduled',
            label: STATUS_LABEL.scheduled,
            detail: `${formatKoreaDateTime(popup.starts_at)}부터 자동으로 노출됩니다.`
        };
    }
    if (now > endsAt) {
        return {
            tone: 'expired',
            label: STATUS_LABEL.expired,
            detail: `${formatKoreaDateTime(popup.ends_at)}에 노출이 종료되었습니다.`
        };
    }
    return {
        tone: 'live',
        label: STATUS_LABEL.live,
        detail: `${formatKoreaDateTime(popup.ends_at)}까지 노출됩니다.`
    };
}

export function popupToForm(popup) {
    return {
        content_type: popup?.content_type || 'TEXT',
        title: popup?.title || '',
        body: popup?.body || '',
        enabled: Boolean(popup?.enabled),
        managed_by_event_id: popup?.managed_by_event_id || null,
        starts_at: toLocalDateTime(popup?.starts_at),
        ends_at: toLocalDateTime(popup?.ends_at),
        image_url: popup?.image_url || null,
        version: Number(popup?.version || 1),
        updated_by: popup?.updated_by || null,
        updated_at: popup?.updated_at || null
    };
}

// 콘텐츠(형식·제목·본문·이미지)만 검증한다. 가입혜택 탭은 노출 기간을 이벤트에서
// 받아 오므로 팝업 폼의 기간 입력이 없다 — 기간 검증을 분리해야 그 화면을 검증할 수 있다.
export function validatePopupContent(form, selectedImage) {
    if (form.title.trim().length > 200) return '제목은 200자 이하로 입력해 주세요.';
    if (form.body.length > 5000) return '본문은 5,000자 이하로 입력해 주세요.';

    const needsText = form.content_type === 'TEXT' || form.content_type === 'IMAGE_TEXT';
    const needsImage = form.content_type === 'IMAGE' || form.content_type === 'IMAGE_TEXT';
    if (needsText && !form.body.trim()) return '선택한 팝업 형식에는 본문이 필요합니다.';
    if (needsImage && !selectedImage && !form.image_url) return '선택한 팝업 형식에는 이미지가 필요합니다.';
    return '';
}

export function validatePopupForm(form, selectedImage) {
    if (!form.starts_at || !form.ends_at) return '노출 시작·종료 일시를 모두 입력해 주세요.';
    if (form.starts_at > form.ends_at) return '종료 일시는 시작 일시보다 빠를 수 없습니다.';
    return validatePopupContent(form, selectedImage);
}

function PopupSettings() {
    // 권한 소스는 PermissionGuard와 동일한 outlet context다. 슬롯 탭은 popup_manage,
    // 가입혜택관리 탭은 benefit_event_manage로 각각 게이트한다 (서버 인가와 동일 분리).
    const { permissions, role } = useOutletContext() || {};
    const canManagePopups = role === 'admin' || Boolean(permissions?.popup_manage);
    const canManageBenefit = role === 'admin' || Boolean(permissions?.benefit_event_manage);
    const visiblePlacements = useMemo(
        () => Object.keys(PLACEMENT_META).filter(
            (placement) => placement === 'PRE_LOGIN_EVENT' ? canManageBenefit : canManagePopups
        ),
        [canManagePopups, canManageBenefit]
    );

    const [popups, setPopups] = useState([]);
    const [activePlacement, setActivePlacement] = useState(canManagePopups ? 'PRE_LOGIN' : 'PRE_LOGIN_EVENT');
    const [form, setForm] = useState(null);
    const [savedForm, setSavedForm] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loading, setLoading] = useState(canManagePopups);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const fileInputRef = useRef(null);

    const applyPopup = (popup) => {
        const nextForm = popupToForm(popup);
        setForm(nextForm);
        setSavedForm(nextForm);
        setSelectedImage(null);
        setPreviewUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // silent: 이벤트 전이 후 재동기화용 — 전면 로딩 패널로 임베드한 이벤트 관리가 언마운트되지 않게 한다.
    const loadPopups = async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setError('');
        try {
            const response = await axios.get('/api/admin/popups');
            const loaded = response.data.popups || [];
            setPopups(loaded);
            const active = loaded.find((popup) => popup.placement === activePlacement) || loaded[0];
            if (active) {
                setActivePlacement(active.placement);
                applyPopup(active);
            }
        } catch (loadError) {
            console.error('팝업 설정 조회 오류:', loadError);
            setError(loadError.response?.data?.message || '팝업 설정을 불러오지 못했습니다.');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    // 이벤트 저장·상태 전이는 "가입혜택 팝업"의 노출·기간을 서버에서 바꾼다. 재조회하지 않으면
    // 낡은 form이 남아 다음 저장이 종료된 팝업을 되살린다(서버 가드는 관리 중에만 유효).
    const refreshPopupsAfterEvent = () => {
        if (canManagePopups) loadPopups({ silent: true });
    };

    useEffect(() => {
        // popup_manage가 없는 계정은 팝업 목록 API가 403이라 호출하지 않는다.
        if (canManagePopups) loadPopups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    const comparable = (value) => value && ({
        content_type: value.content_type,
        title: value.title,
        body: value.body,
        enabled: value.enabled,
        starts_at: value.starts_at,
        ends_at: value.ends_at
    });
    const isDirty = Boolean(selectedImage) || JSON.stringify(comparable(form)) !== JSON.stringify(comparable(savedForm));
    const validationError = form ? validatePopupForm(form, selectedImage) : '';

    const imageSource = useMemo(() => previewUrl || form?.image_url || null, [previewUrl, form?.image_url]);
    const activeSchedule = useMemo(() => getPopupScheduleState(form), [form]);

    const updateField = (field, value) => {
        setError('');
        setNotice('');
        setForm((current) => ({ ...current, [field]: value }));
        if (field === 'content_type' && value === 'TEXT') {
            setSelectedImage(null);
            setPreviewUrl(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const selectPlacement = (placement) => {
        if (placement === activePlacement) return;
        if (isDirty && !window.confirm('저장하지 않은 변경사항이 있습니다. 다른 위치로 이동하시겠습니까?')) return;
        const popup = popups.find((item) => item.placement === placement);
        setActivePlacement(placement);
        setError('');
        setNotice('');
        if (popup) applyPopup(popup);
    };

    const resetChanges = () => {
        const popup = popups.find((item) => item.placement === activePlacement);
        if (popup) applyPopup(popup);
        setError('');
        setNotice('');
    };

    const handleImageChange = (event) => {
        const file = event.target.files?.[0] || null;
        setError('');
        setNotice('');
        if (!file) {
            setSelectedImage(null);
            setPreviewUrl(null);
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setError('이미지는 10MB 이하만 업로드할 수 있습니다.');
            event.target.value = '';
            return;
        }
        if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
            setError('JPEG, PNG, GIF, WebP 이미지만 사용할 수 있습니다.');
            event.target.value = '';
            return;
        }
        setSelectedImage(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    // 팝업 PUT은 관리 중(managed_by_event_id)이어도 starts_at·ends_at을 필수로 요구하고
    // 값 자체는 서버가 현재 값으로 덮는다(popupSettingsHandler.js:245, :364). 가입혜택 탭은
    // 기간 입력이 없으므로 이벤트 기간을 실어 보낸다. 실패는 호출자가 처리한다(throw).
    const submitPopup = async ({ startsAt, endsAt } = {}) => {
        const payload = new FormData();
        payload.append('content_type', form.content_type);
        payload.append('title', form.title);
        payload.append('body', form.body);
        payload.append('enabled', String(form.enabled));
        payload.append('starts_at', toApiDateTime(startsAt || form.starts_at));
        payload.append('ends_at', toApiDateTime(endsAt || form.ends_at));
        if (selectedImage) payload.append('image', selectedImage);

        const response = await axios.put(`/api/admin/popups/${activePlacement}`, payload);
        const savedPopup = response.data.popup;
        setPopups((current) => current.map((popup) => popup.placement === activePlacement ? savedPopup : popup));
        applyPopup(savedPopup);
        return response.data;
    };

    const savePopup = async () => {
        const invalidMessage = validatePopupForm(form, selectedImage);
        if (invalidMessage) {
            setError(invalidMessage);
            return;
        }
        if (!window.confirm(`${PLACEMENT_META[activePlacement].popupLabel} 설정을 저장하시겠습니까?`)) return;

        setSaving(true);
        setError('');
        setNotice('');
        try {
            const result = await submitPopup();
            setNotice(result.changed === false ? '변경된 내용이 없습니다.' : '팝업 설정과 변경 이력을 저장했습니다.');
        } catch (saveError) {
            console.error('팝업 설정 저장 오류:', saveError);
            setError(saveError.response?.data?.message || '팝업 설정을 저장하지 못했습니다.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <PageShell><StatePanel>팝업 설정을 불러오는 중입니다.</StatePanel></PageShell>;
    if (canManagePopups && !form) {
        return (
            <PageShell>
                <StatePanel $error>
                    <strong>팝업 설정을 불러오지 못했습니다.</strong>
                    <span>{error || 'DB 마이그레이션 적용 여부를 확인해 주세요.'}</span>
                    <SecondaryButton type="button" onClick={() => loadPopups()}>다시 시도</SecondaryButton>
                </StatePanel>
            </PageShell>
        );
    }

    const activeMeta = PLACEMENT_META[activePlacement];
    const needsText = form?.content_type === 'TEXT' || form?.content_type === 'IMAGE_TEXT';
    const needsImage = form?.content_type === 'IMAGE' || form?.content_type === 'IMAGE_TEXT';
    const scheduleManagedByEvent = Boolean(form?.managed_by_event_id);
    const showBenefitEvents = activePlacement === 'PRE_LOGIN_EVENT' && canManageBenefit;

    // 가입혜택 탭에서는 팝업 편집기를 따로 띄우지 않고, 이벤트 카드 안의 "이벤트 팝업"
    // 섹션이 이 계약으로 같은 상태를 편집한다 — 저장 버튼이 하나여야 하기 때문이다.
    const popupSlot = showBenefitEvents && canManagePopups && form ? {
        form,
        imageSource,
        selectedImage,
        fileInputRef,
        updateField,
        handleImageChange,
        contentError: validatePopupContent(form, selectedImage),
        dirty: isDirty,
        save: submitPopup
    } : null;

    const popupEditor = form && (
        <ContentGrid>
            <SettingsCard>
                <SectionHeader>
                    <div>
                        <SectionTitle>{activeMeta.popupLabel}</SectionTitle>
                        <SectionDescription>{activeMeta.description}</SectionDescription>
                    </div>
                    <SwitchRow>
                        <SwitchCopy>
                            <strong>앱 노출</strong>
                            <span>{form.enabled ? '사용' : '중지'}</span>
                        </SwitchCopy>
                        <SwitchButton
                            type="button"
                            role="switch"
                            aria-checked={form.enabled}
                            aria-label={`${activeMeta.popupLabel} 노출`}
                            $checked={form.enabled}
                            disabled={scheduleManagedByEvent}
                            title={scheduleManagedByEvent ? '가입혜택 이벤트에서 노출 상태를 관리합니다.' : undefined}
                            onClick={() => updateField('enabled', !form.enabled)}
                        >
                            <SwitchKnob $checked={form.enabled} />
                        </SwitchButton>
                    </SwitchRow>
                </SectionHeader>

                {scheduleManagedByEvent && (
                    <StatusMessage role="status">
                        가입혜택 이벤트 #{form.managed_by_event_id}가 노출 상태와 기간을 관리하고 있습니다. 이미지와 문구만 수정할 수 있습니다.
                    </StatusMessage>
                )}

                <FormSection>
                    <SubsectionHeading>
                        <div>
                            <SubsectionTitle>콘텐츠 구성</SubsectionTitle>
                            <SubsectionDescription>앱에서 전달할 메시지 형식을 선택하고 내용을 입력합니다.</SubsectionDescription>
                        </div>
                    </SubsectionHeading>

                    <FieldGroup>
                        <FieldLabel as="span">팝업 형식</FieldLabel>
                        <TypeOptions>
                            {Object.entries(CONTENT_TYPE_LABEL).map(([value, label]) => (
                                <TypeButton
                                    key={value}
                                    type="button"
                                    aria-pressed={form.content_type === value}
                                    $selected={form.content_type === value}
                                    onClick={() => updateField('content_type', value)}
                                >
                                    <strong>{label}</strong>
                                    <small>{CONTENT_TYPE_DESCRIPTION[value]}</small>
                                </TypeButton>
                            ))}
                        </TypeOptions>
                    </FieldGroup>

                    <FieldGroup>
                        <FieldLabel htmlFor="popup-title">제목 <Optional>선택 · 최대 200자</Optional></FieldLabel>
                        <TextInput
                            id="popup-title"
                            maxLength="200"
                            value={form.title}
                            onChange={(event) => updateField('title', event.target.value)}
                            placeholder="팝업 제목을 입력해 주세요."
                        />
                    </FieldGroup>

                    {needsText && (
                        <FieldGroup>
                            <FieldLabel htmlFor="popup-body">본문 <Required>필수</Required></FieldLabel>
                            <TextArea
                                id="popup-body"
                                maxLength="5000"
                                value={form.body}
                                onChange={(event) => updateField('body', event.target.value)}
                                placeholder={'본문을 입력해 주세요.\nhttps:// 로 시작하는 주소는 앱에서 자동으로 링크 처리됩니다.'}
                            />
                            <FieldMetaRow>
                                <FieldHint>HTTP/HTTPS URL은 앱에서 자동으로 링크 처리됩니다.</FieldHint>
                                <CharacterCount $nearLimit={form.body.length >= 4500}>{form.body.length.toLocaleString('ko-KR')} / 5,000</CharacterCount>
                            </FieldMetaRow>
                        </FieldGroup>
                    )}

                    {needsImage && (
                        <FieldGroup>
                            <FieldLabel htmlFor="popup-image">이미지 <Required>필수</Required></FieldLabel>
                            <FileInput
                                ref={fileInputRef}
                                id="popup-image"
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                onChange={handleImageChange}
                            />
                            <UploadArea>
                                <UploadCopy>
                                    <strong>{selectedImage ? selectedImage.name : form.image_url ? '기존 이미지 사용 중' : '이미지를 선택해 주세요'}</strong>
                                    <span>{selectedImage
                                        ? `${(selectedImage.size / 1024 / 1024).toFixed(2)}MB · 저장 시 새 이미지로 교체됩니다.`
                                        : 'JPEG, PNG, GIF, WebP · 최대 10MB'}</span>
                                </UploadCopy>
                                <UploadButton type="button" onClick={() => fileInputRef.current?.click()}>
                                    {selectedImage || form.image_url ? '이미지 변경' : '파일 선택'}
                                </UploadButton>
                            </UploadArea>
                            <FieldHint>새 파일을 선택하지 않으면 기존 이미지를 그대로 유지합니다.</FieldHint>
                        </FieldGroup>
                    )}
                </FormSection>

                <FormSection>
                    <SubsectionHeading>
                        <div>
                            <SubsectionTitle>노출 일정</SubsectionTitle>
                            <SubsectionDescription>설정한 기간 안에서만 팝업이 자동으로 노출됩니다.</SubsectionDescription>
                        </div>
                    </SubsectionHeading>
                    <DateGrid>
                        <FieldGroup>
                            <FieldLabel htmlFor="popup-starts-at">노출 시작 <Required>필수</Required></FieldLabel>
                            <TextInput
                                id="popup-starts-at"
                                type="datetime-local"
                                value={form.starts_at}
                                disabled={scheduleManagedByEvent}
                                onChange={(event) => updateField('starts_at', event.target.value)}
                            />
                        </FieldGroup>
                        <FieldGroup>
                            <FieldLabel htmlFor="popup-ends-at">노출 종료 <Required>필수</Required></FieldLabel>
                            <TextInput
                                id="popup-ends-at"
                                type="datetime-local"
                                value={form.ends_at}
                                disabled={scheduleManagedByEvent}
                                onChange={(event) => updateField('ends_at', event.target.value)}
                            />
                        </FieldGroup>
                    </DateGrid>
                    <ScheduleStrip $tone={activeSchedule.tone}>
                        <StatusDot $tone={activeSchedule.tone} />
                        <div>
                            <strong>{activeSchedule.label}</strong>
                            <span>{activeSchedule.detail}</span>
                        </div>
                        <small>한국시간 기준</small>
                    </ScheduleStrip>
                </FormSection>

                <CardFooter>
                    <FooterMeta>
                        <SaveState $dirty={isDirty}>{isDirty ? '저장 전 변경사항' : '모든 변경사항 저장됨'}</SaveState>
                        <UpdatedInfo>
                            버전 {form.version}
                            {form.updated_by ? ` · ${form.updated_by}` : ''}
                            {form.updated_at ? ` · ${new Date(form.updated_at).toLocaleString('ko-KR')}` : ''}
                        </UpdatedInfo>
                    </FooterMeta>
                    <FooterActions>
                        <ResetButton type="button" onClick={resetChanges} disabled={!isDirty || saving}>변경 취소</ResetButton>
                        <PrimaryButton
                            type="button"
                            onClick={savePopup}
                            disabled={!isDirty || saving || Boolean(validationError)}
                            title={validationError || undefined}
                        >{saving ? '저장 중…' : '설정 저장'}</PrimaryButton>
                    </FooterActions>
                </CardFooter>
            </SettingsCard>

            <PreviewCard>
                <SectionHeader>
                    <div>
                        <SectionTitle>앱 미리보기</SectionTitle>
                        <SectionDescription>입력한 내용이 저장 전에 바로 반영됩니다.</SectionDescription>
                    </div>
                    <VisibilityBadge $tone={activeSchedule.tone}><i />{activeSchedule.label}</VisibilityBadge>
                </SectionHeader>
                <PreviewStage>
                    <PreviewViewport>
                        <PreviewBar>
                            <PreviewBrand><i /> AirconRG</PreviewBrand>
                            <span>{activeMeta.popupLabel}</span>
                        </PreviewBar>
                        <PreviewCanvas>
                            <PopupPreview>
                                {needsImage && (imageSource
                                    ? <PreviewImage src={imageSource} alt="팝업 미리보기" />
                                    : <ImagePlaceholder>이미지를 선택하면<br />이곳에 표시됩니다.</ImagePlaceholder>)}
                                {form.title && <PreviewTitle>{form.title}</PreviewTitle>}
                                {needsText && (
                                    <PreviewBody><LinkifiedText>{form.body || '본문 미리보기'}</LinkifiedText></PreviewBody>
                                )}
                                <PreviewAction type="button">확인</PreviewAction>
                            </PopupPreview>
                        </PreviewCanvas>
                    </PreviewViewport>
                </PreviewStage>
                <PreviewNote>
                    <strong>링크 열기 안내</strong>
                    본문 URL은 앱의 Universal Link/App Link 설정에 따라 앱 내부 또는 브라우저에서 열립니다.
                </PreviewNote>
            </PreviewCard>
        </ContentGrid>
    );

    return (
        <PageShell>
            <PageHeader>
                <HeaderCopy>
                    <PageTitle>팝업관리</PageTitle>
                    <PageDescription>앱 노출 위치별 콘텐츠, 기간과 노출 상태를 관리합니다.</PageDescription>
                </HeaderCopy>
            </PageHeader>

            <PlacementTabs role="tablist" aria-label="팝업 노출 위치">
                {visiblePlacements.map((placement) => {
                    const meta = PLACEMENT_META[placement];
                    const popup = popups.find((item) => item.placement === placement);
                    const popupState = getPopupScheduleState(popup);
                    return (
                        <PlacementTab
                            key={placement}
                            type="button"
                            role="tab"
                            aria-selected={activePlacement === placement}
                            $active={activePlacement === placement}
                            onClick={() => selectPlacement(placement)}
                        >
                            <TabLabel>{meta.label}</TabLabel>
                            {canManagePopups && (
                                <TabStatus $tone={popupState.tone} title={popupState.label}><i /></TabStatus>
                            )}
                        </PlacementTab>
                    );
                })}
            </PlacementTabs>

            {(error || notice) && (
                <StatusMessage $error={Boolean(error)} role={error ? 'alert' : 'status'}>{error || notice}</StatusMessage>
            )}

            {showBenefitEvents
                ? <BenefitEvents embedded popupSlot={popupSlot} onEventsChanged={refreshPopupsAfterEvent} />
                : popupEditor}
        </PageShell>
    );
}

export default PopupSettings;
